import React from "react";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import { PrimaryLink } from "components/Links";
import { APPLICANT_TYPES, ROLE_STATES_LABELS } from "constants/index";
import { Row, Col } from "components/Containers";
import { RoleLink, RolePipeline } from "components/Missions/Roles";
import { formatCurrency, formatNumber, getApiLimitSuffix, isEmpty } from "utils";
import Tags from "components/Tags";
import Status from "components/Status";
import { TAG_COLORS, TAG_SIZE } from "components/Tags/Tag";
import { OrganizationLink } from "components/Organization";
import { ActionButton } from "components/Buttons";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";
import { colors } from "style";
import MISSION_ROLE_STATES from "constants/missionRoleStates";

const STATUS_COLORS = {
  [MISSION_ROLE_STATES.draft]: {
    color: colors.lightBlue,
    background: colors.lightestBlue,
    dotColor: colors.blueVelvetBase,
  },
  [MISSION_ROLE_STATES.draftInProgress]: {
    color: colors.grayAnatomyLight6,
    background: colors.grayAnatomyLight4,
    dotColor: colors.grayAnatomyLight1,
  },
  [MISSION_ROLE_STATES.published]: {
    color: colors.darkGreen,
    background: colors.lightGreen,
    dotColor: colors.green,
  },
  [MISSION_ROLE_STATES.unpublished]: {
    color: colors.darkRed,
    background: colors.lightRed,
    dotColor: colors.red,
  },
  [MISSION_ROLE_STATES.closed]: {
    color: colors.darkRed,
    background: colors.lightRed,
    dotColor: colors.red,
  },
};

const MISSION_ROLES_COLUMNS = {
  role: ({ links }) => ({
    label: <FormattedMessage id="MissionBoard.TableColumnRole" />,
    cell: (row) => (
      <Col gap="0.25rem">
        <span>
          <RoleLink mission={row?.mission} missionRole={row} withOverlay />
        </span>
        <span>
          <FormattedMessage
            id="MissionBoard.TableColumnNumApplicants"
            values={{
              formattedNumber: formatNumber(row?.totalApplicantsCount, getApiLimitSuffix(row?.totalApplicantsCount)),
              count: row?.totalApplicantsCount,
              link: (
                <PrimaryLink
                  to={links.mission_role_applications(row?.mission?.uuid, row?.slug)}
                  state={{ missionRole: row }}
                  routerEnabled
                >
                  <FormattedMessage id="Global.View" />
                </PrimaryLink>
              ),
            }}
          />
        </span>
      </Col>
    ),
    width: "100%",
  }),
  skillsAndDisciplines: {
    label: <FormattedMessage id="MissionBoard.TableColumnSkillsAndDisciplines" />,
    cell: (row) => (
      <Row gap="0.25rem">
        <Tags data={row?.disciplines} limit={3} unwrap {...TAG_COLORS.purple} size={TAG_SIZE.small} />
        <Tags data={row?.primarySkills} limit={3} unwrap {...TAG_COLORS.pink} size={TAG_SIZE.small} />
      </Row>
    ),
    width: "100%",
  },
  matchingProfiles: {
    label: <FormattedMessage id="MissionBoard.TableColumnMatchingProfiles" />,
    cell: ({ matchingProfilesCount }) => (
      <div
        css={css`
          text-align: center;
        `}
      >
        {isEmpty(matchingProfilesCount) ? "-" : matchingProfilesCount}
      </div>
    ),
    width: 80,
  },
  organization: {
    label: <FormattedMessage id="MissionBoard.TableColumnClient" />,
    cell: (row) => <OrganizationLink data={row?.mission?.organization} withOverlay />,
    width: "100%",
  },
  talentOwner: {
    label: <FormattedMessage id="MissionBoard.TableColumnTalentOwner" />,
    cell: (row) => row?.talentOwner?.name || <FormattedMessage id="Global.EmDash" />,
    width: 100,
  },
  salesOwner: {
    label: <FormattedMessage id="MissionBoard.TableColumnSalesOwner" />,
    cell: (row) => row?.salesOwner?.name || <FormattedMessage id="Global.EmDash" />,
    width: 100,
  },
  targetRate: {
    label: <FormattedMessage id="MissionBoard.TableColumnTargetRate" />,
    cell: (row) => formatCurrency(row?.targetRate, row?.targetRateCurrency),
    width: 100,
  },
  hours: {
    label: <FormattedMessage id="MissionBoard.TableColumnHours" />,
    cell: (row) => row?.engagementTime,
    width: 60,
  },
  status: {
    label: <FormattedMessage id="MissionBoard.TableColumnStatus" />,
    cell: (row) => (
      <Status
        color={STATUS_COLORS[row.state]?.color}
        background={STATUS_COLORS[row.state]?.background}
        dotColor={STATUS_COLORS[row.state]?.dotColor}
      >
        {ROLE_STATES_LABELS[row.state]}
      </Status>
    ),
    width: "100%",
  },
  pipeline: {
    label: <FormattedMessage id="Missions.Roles.TablePipeline" />,
    width: 200,
    cell: (row) => (
      <RolePipeline
        data={[
          row?.pendingCandidatesCount,
          row?.applicantsCount,
          row?.shortlistedCandidatesCount,
          row?.interviewedCandidatesCount,
          row?.selectedCandidatesCount,
        ]}
        tooltipData={[
          APPLICANT_TYPES.pending,
          APPLICANT_TYPES.applied,
          APPLICANT_TYPES.shortlisted,
          APPLICANT_TYPES.interviewed,
          APPLICANT_TYPES.selected,
        ].map((item) => `Missions.MissionCandidateList.State.${item}`)}
      />
    ),
  },
  viewButton: ({ navigate, links }) => ({
    width: 60,
    cell: (row) => {
      const slug = row?.slug;
      const missionUuid = row?.mission?.uuid;
      const link = links.mission_role_applications(missionUuid, slug);

      return (
        <ActionButton onClick={() => navigate(link, { state: { missionRole: row } })}>
          <Icon type={ICON_TYPE.eye} size={ICON_SIZE.large} color={colors.purpleRainBase} />
        </ActionButton>
      );
    },
  }),
};

export default MISSION_ROLES_COLUMNS;

export { STATUS_COLORS };
